import DOMPurify from 'dompurify';

const EXEC_COMMAND_MAP = {
  'menu-item-strong-option': { cmd: 'bold' },
  'menu-item-em-option': { cmd: 'italic' },
  'menu-item-bulletList-option': { cmd: 'insertUnorderedList' },
  'menu-item-orderedList-option': { cmd: 'insertOrderedList' },
  'menu-item-alignLeft-option': { cmd: 'justifyLeft' },
  'menu-item-alignCenter-option': { cmd: 'justifyCenter' },
  'menu-item-alignRight-option': { cmd: 'justifyRight' },
  'menu-item-alignJustify-option': { cmd: 'justifyFull' },
};

function matchCommandClass(element) {
  const classes = Object.keys(EXEC_COMMAND_MAP);
  const matched = classes.find(cls => element.classList?.contains(cls));
  return matched ? EXEC_COMMAND_MAP[matched] : null;
}

export function resolveExecCommand(target, root) {
  let el = target;
  while (el && el !== root) {
    const classMatch = matchCommandClass(el);
    if (classMatch) return classMatch;
    el = el.parentElement;
  }

  const dropdownItem = target.closest('.ProseMirror-menu-dropdown-item');
  if (dropdownItem) {
    const wrap = dropdownItem.closest('.ProseMirror-menu-dropdown-wrap');
    if (!wrap) return null;

    if (wrap.querySelector('.menu-item-fontFamily-option')) {
      const name = dropdownItem.textContent?.trim();
      if (name) return { cmd: 'fontName', value: name };
    }

    if (wrap.querySelector('.menu-item-textColor-option')) {
      const btn =
        dropdownItem.querySelector('button') ||
        (dropdownItem.tagName === 'BUTTON' ? dropdownItem : null);
      if (btn?.style.backgroundColor) {
        return { cmd: 'foreColor', value: btn.style.backgroundColor };
      }
    }
  }

  const sizeBtn = target.closest('.ProseMirror-menu-fontsize-btn');
  if (sizeBtn) {
    return {
      cmd: 'fontSize',
      isIncrease: sizeBtn.title?.includes('Increase'),
    };
  }

  return null;
}

function applyFontSize(isIncrease, editorEl, blockDom) {
  const sel = window.getSelection();
  if (sel.rangeCount === 0 || sel.isCollapsed) return;

  const anchor = sel.anchorNode?.parentElement;
  const current =
    (anchor && parseInt(window.getComputedStyle(anchor).fontSize, 10)) || 16;
  const size = Math.max(8, Math.min(96, current + (isIncrease ? 1 : -1)));

  document.execCommand('styleWithCSS', false, true);
  document.execCommand('fontSize', false, 7);

  if (blockDom) {
    blockDom.querySelectorAll('span[style]').forEach(span => {
      if (span.style.fontSize.includes('xxx-large')) {
        span.style.fontSize = `${size}px`;
      }
    });
  }

  const input = editorEl?.querySelector('.ProseMirror-menu-fontsize-input');
  if (input) input.value = size;
}

export default class HtmlBlockView {
  constructor(node, view, getPos) {
    this.dom = document.createElement('div');
    this.dom.classList.add('html-block-content');
    this.dom.setAttribute('contenteditable', 'true');
    this.dom.innerHTML = DOMPurify.sanitize(node.attrs.html);
    this.node = node;
    this.view = view;
    this.getPos = getPos;
    this.savedRange = null;

    this.onMousedown = e => e.stopPropagation();
    this.onMouseup = () => this.saveSelection();
    this.onKeyup = () => this.saveSelection();
    this.dom.addEventListener('mousedown', this.onMousedown);
    this.dom.addEventListener('mouseup', this.onMouseup);
    this.dom.addEventListener('keyup', this.onKeyup);
  }

  saveSelection() {
    const sel = window.getSelection();
    if (sel.rangeCount > 0 && this.dom.contains(sel.anchorNode)) {
      this.savedRange = sel.getRangeAt(0).cloneRange();
    }
  }

  restoreAndExec(result, editorEl) {
    if (document.activeElement !== this.dom) {
      this.dom.focus();
      if (this.savedRange) {
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(this.savedRange);
      }
    }

    if (result.cmd === 'fontSize') {
      applyFontSize(result.isIncrease, editorEl, this.dom);
    } else {
      document.execCommand('styleWithCSS', false, true);
      document.execCommand(result.cmd, false, result.value ?? null);
    }
    this.saveSelection();
  }

  stopEvent() {
    return true;
  }

  ignoreMutation() {
    return true;
  }

  update(node) {
    if (node.type !== this.node.type) return false;
    if (node.attrs.html !== this.node.attrs.html) {
      this.dom.innerHTML = DOMPurify.sanitize(node.attrs.html);
    }
    this.node = node;
    return true;
  }

  destroy() {
    this.dom.removeEventListener('mousedown', this.onMousedown);
    this.dom.removeEventListener('mouseup', this.onMouseup);
    this.dom.removeEventListener('keyup', this.onKeyup);
  }

  getHtml() {
    return this.dom.innerHTML;
  }
}
